"use client";

import { useCallback, useRef, useState, type ComponentPropsWithRef, type FC } from "react";
import { cn } from "@/lib/cn";

type ScrollAreaProps = ComponentPropsWithRef<"div"> & {
  viewportClassName?: string;
  viewportProps?: ComponentPropsWithRef<"div">;
  orientation?: "vertical" | "horizontal" | "both";
};

const overflowClasses = {
  vertical: "overflow-y-auto overflow-x-hidden",
  horizontal: "overflow-x-auto overflow-y-hidden",
  both: "overflow-auto",
} as const;

const HIDE_DELAY = 800;

/* Native scrollbars, styled by the `scrollbar-slim` utility (globals.css) and hidden while idle
   via `data-scrolling`. The previous Radix implementation repositioned its thumb from a rAF
   polling loop, which starves under compositor load and freezes the thumb mid-scroll; the
   browser-drawn thumb cannot desync. The inner div is kept so `[&>div]` viewport classes at call
   sites (e.g. the drawer's h-full chain) keep working. */
export const ScrollArea: FC<ScrollAreaProps> = ({
  className,
  viewportClassName,
  viewportProps,
  orientation = "vertical",
  children,
  ...props
}) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      setIsScrolling(true);
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => setIsScrolling(false), HIDE_DELAY);
      viewportProps?.onScroll?.(event);
    },
    [viewportProps],
  );

  return (
    <div className={cn("overflow-hidden", className)} {...props}>
      <div
        className={cn("size-full rounded-[inherit] scrollbar-slim", overflowClasses[orientation], viewportClassName)}
        data-scrolling={isScrolling || undefined}
        {...viewportProps}
        onScroll={handleScroll}
      >
        <div>{children}</div>
      </div>
    </div>
  );
};
